import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { SaleBidderLevel } from '__generated__/globalTypes';
import {
  describeLoanTableFailure,
  describeLoanTableRefusal,
  openTableView,
  type FailureDescription,
  type KeyServiceConfig,
  type OpenedTableView,
} from 'features/irm/core';
import { getIdToken } from 'features/irm/idToken';
import { firstGraphQLError } from 'utils/apolloGraphQLError';

import {
  OpenSalePoolLoanTable,
  OpenSalePoolLoanTableVariables,
} from 'mutation/__generated__/salePoolLoanTable';
import { OPEN_SALE_POOL_LOAN_TABLE } from 'mutation/salePoolLoanTable';

export type LoanTableSession = OpenSalePoolLoanTable['openSalePoolLoanTable'];

export type BidderLoanTableState =
  | { name: 'unconfigured' }
  | { name: 'notice' }
  | { name: 'declined' }
  | { name: 'opening' }
  | {
      name: 'open';
      session: LoanTableSession;
      view: OpenedTableView;
      accessEndsAtWallMs: number;
      suspended: boolean;
      piiNoticeVersion?: string;
    }
  | { name: 'stopped'; failure: FailureDescription };

const PII_NOTICE_REQUIRED_CODE = 'PII_NOTICE_REQUIRED';

// api words these refusals for the bidder, with createProtectedDownload's extensions; any other
// api message may be internal.
const BIDDER_WORDED_REFUSALS = new Set(['FORBIDDEN', 'BAD_USER_INPUT', 'NOT_FOUND']);

// As loanPageError: these need a fresh sign-in, which the refusal copy offers and api's message doesn't.
const SIGN_IN_REASONS = new Set(['reauth_required', 'unauthenticated']);

// protect's registerTableView answers in file-download wording, which api passes on as it does for
// a protected download; these say the same for the loan table.
export const LOAN_TABLE_OPEN_NOT_PERMITTED: FailureDescription = {
  title: "This loan data can't be opened",
  body: "Your access to this sale doesn't allow opening this pool's loan data. Contact the sale's administrator if you think it should.",
  next: 'none',
};

export const LOAN_TABLE_OPEN_TRY_AGAIN: FailureDescription = {
  title: "This loan data can't be opened right now",
  body: "We couldn't open this pool's loan data just now. Try again in a moment.",
  next: 'retry',
};

export const LOAN_TABLE_OPEN_FAILED: FailureDescription = {
  title: "This loan data can't be opened",
  body: "Something went wrong opening this pool's loan data. Contact the sale's administrator if this keeps happening.",
  next: 'none',
};

// api serves the notice only to a bidder with Level 2 access now, so showing it again can't help.
export const LOAN_TABLE_NOTICE_REFUSED: FailureDescription = {
  title: "This loan data can't be opened",
  body: "You don't have Level 2 access to an open sale now, so this pool's Level 2 loan data can't be opened.",
  next: 'none',
};

const apiErrorCode = (error: unknown): unknown => firstGraphQLError(error)?.extensions?.code;

function openFailure(error: unknown): FailureDescription {
  const apiError = firstGraphQLError(error);
  const code = String(apiErrorCode(error));
  const reason = String(apiError?.extensions?.reason ?? '');
  const retryable = apiError?.extensions?.retryable === true;
  switch (code) {
    case 'ACCESS_REVOKED':
      return describeLoanTableRefusal(reason);
    case 'PROTECTED_DOWNLOAD_TIMEOUT':
    case 'PROTECTED_DOWNLOAD_FAILED':
      return retryable ? LOAN_TABLE_OPEN_TRY_AGAIN : LOAN_TABLE_OPEN_FAILED;
    case 'FORBIDDEN':
      if (reason === 'policy_denied') return LOAN_TABLE_OPEN_NOT_PERMITTED;
      if (SIGN_IN_REASONS.has(reason)) return describeLoanTableRefusal(reason);
      break;
    case 'BAD_USER_INPUT':
      if (reason === 'invalid_request') return LOAN_TABLE_OPEN_FAILED;
      break;
  }
  if (!apiError?.message || !BIDDER_WORDED_REFUSALS.has(code)) {
    return describeLoanTableFailure(error);
  }
  return {
    title: "This loan data can't be opened",
    body: apiError.message,
    next: retryable ? 'retry' : 'none',
  };
}

class UnprotectedSessionError extends Error {
  constructor() {
    super('api returned no protected view for a bidder');
    this.name = 'UnprotectedSessionError';
  }
}

// Exhibit 6: the Special PII Notice before every Level 2 open, never remembered between opens. A
// level this client doesn't know gets it too.
const needsPiiNotice = (level: SaleBidderLevel | null) => level !== SaleBidderLevel.L1;

export function useBidderLoanTableSession(
  saleId: string,
  offeringId: string,
  level: SaleBidderLevel | null,
  keyService: KeyServiceConfig | undefined,
) {
  const [state, setState] = useState<BidderLoanTableState>(() => {
    if (!keyService) return { name: 'unconfigured' };
    return needsPiiNotice(level) ? { name: 'notice' } : { name: 'opening' };
  });
  const [openLoanTable] = useMutation<OpenSalePoolLoanTable, OpenSalePoolLoanTableVariables>(
    OPEN_SALE_POOL_LOAN_TABLE,
  );
  const attempt = useRef<AbortController>();
  const opened = useRef<OpenedTableView>();

  const endCurrent = useCallback(() => {
    attempt.current?.abort();
    attempt.current = undefined;
    opened.current?.close();
    opened.current = undefined;
  }, []);

  const open = useCallback(
    async (piiNoticeVersion?: string) => {
      if (!keyService) return;
      endCurrent();
      const mine = new AbortController();
      attempt.current = mine;
      let lockedWhileOpening = false;
      let suspended = false;
      setState({ name: 'opening' });

      try {
        const { data } = await openLoanTable({
          variables: {
            saleId,
            offeringId,
            ...(piiNoticeVersion !== undefined && {
              piiNoticeAcknowledged: true,
              piiNoticeVersion,
            }),
          },
        });
        if (mine.signal.aborted) return;
        const session = data?.openSalePoolLoanTable;
        if (!session?.protected || !session.view_id) throw new UnprotectedSessionError();

        const view = await openTableView(
          session.view_id,
          {
            config: keyService,
            getIdToken,
            signal: mine.signal,
            onLock: (why) => {
              if (mine.signal.aborted) return;
              lockedWhileOpening = true;
              opened.current = undefined;
              setState({ name: 'stopped', failure: describeLoanTableFailure(why) });
            },
            onSuspend: () => {
              if (mine.signal.aborted) return;
              suspended = true;
              setState((current) =>
                current.name === 'open' ? { ...current, suspended: true } : current,
              );
            },
            onResume: () => {
              if (mine.signal.aborted) return;
              suspended = false;
              setState((current) =>
                current.name === 'open' ? { ...current, suspended: false } : current,
              );
            },
            onAccessEndsAt: (wallMs) => {
              if (mine.signal.aborted) return;
              setState((current) =>
                current.name === 'open' ? { ...current, accessEndsAtWallMs: wallMs } : current,
              );
            },
          },
          piiNoticeVersion,
        );
        if (mine.signal.aborted || lockedWhileOpening) {
          view.close();
          return;
        }
        opened.current = view;
        setState({
          name: 'open',
          session,
          view,
          accessEndsAtWallMs: view.accessEndsAtWallMs,
          suspended,
          piiNoticeVersion,
        });
      } catch (error) {
        if (mine.signal.aborted) return;
        if (apiErrorCode(error) === PII_NOTICE_REQUIRED_CODE) {
          setState({ name: 'notice' });
          return;
        }
        setState({ name: 'stopped', failure: openFailure(error) });
      }
    },
    [endCurrent, keyService, offeringId, openLoanTable, saleId],
  );

  const openOnEntry = useRef(open);
  openOnEntry.current = open;
  useEffect(() => {
    if (!keyService) setState({ name: 'unconfigured' });
    else if (needsPiiNotice(level)) setState({ name: 'notice' });
    else void openOnEntry.current();
    return endCurrent;
  }, [endCurrent, keyService, level, offeringId, saleId]);

  const acceptNotice = useCallback((version: string) => open(version), [open]);
  const declineNotice = useCallback(() => setState({ name: 'declined' }), []);
  const closeRefusedNotice = useCallback(
    () => setState({ name: 'stopped', failure: LOAN_TABLE_NOTICE_REFUSED }),
    [],
  );
  const reopen = useCallback(() => {
    endCurrent();
    if (needsPiiNotice(level)) setState({ name: 'notice' });
    else void open();
  }, [endCurrent, level, open]);
  // api refused a page of the open view: the rows go and the view closes, as a heartbeat refusal does.
  const lock = useCallback(
    (failure: FailureDescription) => {
      endCurrent();
      setState({ name: 'stopped', failure });
    },
    [endCurrent],
  );
  return { state, acceptNotice, declineNotice, closeRefusedNotice, reopen, lock };
}
